import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computed } from 'mobx';
import { createAppSplitButton, getKeytipString, constructKeytip } from 'owa-command-ribbon';
import {
    getDefaultSlrAppRibbonButtonStyles,
    getSlrHeroAppSplitButtonStyles,
} from 'owa-command-ribbon-styles';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getMailMenuItemShouldShowMap, MenuItemType } from 'owa-mail-filterable-menu-behavior';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { getPalette } from 'owa-theme';
import {
    onArchiveClicked,
    onCreateRuleClicked,
    onDeleteClicked,
    onFlagUnflagClicked,
    onIgnoreStopIgnoreClicked,
    onMarkAsReadUnreadClicked,
    onNewEmailSplitButtonExecuted,
    onNewMessage,
    onNewNoteClicked,
    onPinUnpinClicked,
    onPrintClicked,
    onRibbonCustomizerClicked,
    onShowInImmersiveReaderClicked,
    onSweepClicked,
    onToggleFolderPane,
    onUndoClicked,
} from '../../../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from '../../../../util/createMailRibbonButton';
import { getToolbarRibbonIconColor } from '../../../../util/getToolbarRibbonIconColor';
import {
    getAssignPolicySections,
    getCategorizeSections,
    getFlagUnflagSections,
    getJunkSections,
    getNewGroupSections,
    getSnoozeSections,
    resetCategorizeSearch,
} from '../../../sections';

/**
 * This file contains the definitions for all the controls
 * in the toolbar ribbon
 */

const computeLeftToggleControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ToggleLeftPane);
        return createMailAppButton(
            MailRibbonControlId.ToggleLeftPane,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onToggleFolderPane,
            undefined /* keytipProps */,
            controlInfo.iconName
        );
    }
);
export const getLeftToggleControl = () => computeLeftToggleControl.get();

const computeNewMessageControl = computed(
    (): RibbonControlProps => {
        const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
        const controlInfo = getControlInfo(MailRibbonControlId.NewMessageSplitButton);
        const isNote = shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote]();

        // This should really be calling createMailAppSplitButton(),
        // but there is a WAC bug which overwrites styles rather than
        // merging them.  Fortuante, we don't REALLY need the merge in this case.
        return createAppSplitButton(
            MailRibbonControlId.NewMessageSplitButton,
            controlInfo.text,
            {
                sections: [
                    {
                        controls: getNewGroupSections(undefined),
                    },
                ],
            },
            isNote ? onNewNoteClicked : onNewMessage,
            isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [],
                      getKeytipString(MailRibbonControlId.NewMessageSplitButton),
                      true /* hasMenu */
                  )
                : undefined,
            controlInfo.iconName,
            getPalette().white,
            getSlrHeroAppSplitButtonStyles(
                false /*isToolbarComposeStyle*/,
                undefined /*rightMarginConstants*/
            ),
            {
                customTooltip: isFeatureEnabled('mon-ribbon-tooltips')
                    ? controlInfo.tooltip
                    : undefined,
                onMenuButtonExecute: onNewEmailSplitButtonExecuted,
                primaryDisabled:
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewMessage]() &&
                    !shouldShowBehaviorMap[MenuItemType.Ribbon_NewNote](),
                splitButtonAriaLabel: controlInfo.ariaText,
            }
        );
    }
);
export const getNewMessageControl = () => computeNewMessageControl.get();

const computeDeleteControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.DeleteItem);
        return createMailAppButton(
            MailRibbonControlId.DeleteItem,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onDeleteClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.DeleteItem)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getDeleteControl = () => computeDeleteControl.get();

const computeArchiveControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Archive);
        return createMailAppButton(
            MailRibbonControlId.Archive,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onArchiveClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.Archive)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getArchiveControl = () => computeArchiveControl.get();

const computeSweepControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Sweep);
        return createMailAppButton(
            MailRibbonControlId.Sweep,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onSweepClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.Sweep)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getSweepControl = () => computeSweepControl.get();

const computeJunkControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.JunkFlyout);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.JunkFlyout,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            {
                sections: [
                    {
                        controls: getJunkSections(undefined, getToolbarRibbonIconColor()),
                    },
                ],
            },
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.JunkFlyout),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getJunkControl = () => computeJunkControl.get();

const computeIgnoreStopIgnoreControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.IgnoreStopIgnore);
        return createMailAppButton(
            MailRibbonControlId.IgnoreStopIgnore,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onIgnoreStopIgnoreClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.IgnoreStopIgnore)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getIgnoreStopIgnoreControl = () => computeIgnoreStopIgnoreControl.get();

const computeCreateRulesControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.CreateRule);
        return createMailAppButton(
            MailRibbonControlId.CreateRule,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onCreateRuleClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.CreateRule)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getCreateRulesControl = () => computeCreateRulesControl.get();

const computeReadUnreadControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReadUnread);
        return createMailAppButton(
            MailRibbonControlId.ReadUnread,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onMarkAsReadUnreadClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.ReadUnread)),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getReadUnreadControl = () => computeReadUnreadControl.get();

const computeCategorizeControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Categorize);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Categorize,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getCategorizeSections(undefined) },
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.Categorize),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles(),
            {
                onAfterMenuDismiss: () => {
                    resetCategorizeSearch();
                },
            }
        );
    }
);
export const getCategorizeControl = () => computeCategorizeControl.get();

const computeFlagUnflagControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.FlagUnflag);
        return createMailAppSplitButton(
            MailRibbonControlId.FlagUnflag,
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getFlagUnflagSections() },
            onFlagUnflagClicked(),
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.FlagUnflag),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor()
            // getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getFlagUnflagControl = () => computeFlagUnflagControl.get();

const computePinUnpinControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.PinUnpin);
        return createMailAppButton(
            MailRibbonControlId.PinUnpin,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onPinUnpinClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.PinUnpin)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getPinUnpinControl = () => computePinUnpinControl.get();

const computeSnoozeControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Snooze);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Snooze,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getSnoozeSections() },
            constructKeytip([], getKeytipString(MailRibbonControlId.Snooze), true /* hasMenu */),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getSnoozeControl = () => computeSnoozeControl.get();

const computeAssignPolicyControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.AssignPolicy);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.AssignPolicy,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            { sections: getAssignPolicySections() },
            constructKeytip(
                [],
                getKeytipString(MailRibbonControlId.AssignPolicy),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getAssignPolicyControl = () => computeAssignPolicyControl.get();

const computeUndoControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Undo);
        return createMailAppButton(
            MailRibbonControlId.Undo,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onUndoClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.Undo)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getUndoControl = () => computeUndoControl.get();

const computeImmersiveReaderControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ImmersiveReader);
        return createMailAppButton(
            MailRibbonControlId.ImmersiveReader,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onShowInImmersiveReaderClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.ImmersiveReader)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getImmersiveReaderControl = () => computeImmersiveReaderControl.get();

const computePrintControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Print);
        return createMailAppButton(
            MailRibbonControlId.Print,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onPrintClicked,
            constructKeytip([], getKeytipString(MailRibbonControlId.Print)),
            controlInfo.iconName,
            getToolbarRibbonIconColor(),
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getPrintControl = () => computePrintControl.get();

const computeRibbonCustomizerControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RibbonCustomizer);
        return createMailAppButton(
            MailRibbonControlId.RibbonCustomizer,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            () => {
                onRibbonCustomizerClicked();
            },
            constructKeytip([], getKeytipString(MailRibbonControlId.RibbonCustomizer)),
            undefined /* icon */,
            undefined /* iconColor */,
            getDefaultSlrAppRibbonButtonStyles()
        );
    }
);
export const getRibbonCustomizerControl = () => computeRibbonCustomizerControl.get();
